import React from 'react';
import { useParallax } from 'react-scroll-parallax';

/* ---------------- SignatureDishes1 ---------------- */
const SignatureDishes1: React.FC = () => (
  <div
    className="
      grid 
      grid-cols-1 lg:grid-cols-4 
      gap-4 
      h-[100vh] 
      lg:h-[50vh]       /* h-auto on mobile, 50vh on md+ */
      min-h-0        
      overflow-hidden 
      px-4 lg:px-24 
      pt-4 lg:pt-16
      relative
    "
  >
    {/* Column 1 (Images) 
        - single column on mobile => so we rely on the col-span only on md+ */}
    <div className="lg:col-span-2 min-h-0">
      <img
        src="https://santaplaya-bucket.s3.amazonaws.com/less_is_more.webp"
        alt="Placeholder"
        className="h-full w-full object-cover lg:h-full"
      />
    </div>

    {/* Column 2 (Title + Text) */}
    <div
      className="
        lg:col-span-1 
        bg-sea 
        p-4 lg:p-8 
        flex 
        flex-col 
        h-full 
        min-h-0 
        overflow-hidden
        space-y-4
      "
    >
      <h2 className="text-3xl xl:text-5xl font-bold font-charles text-basic text-center mb-2 mt-4 xl:mt-12">
        Baja Fish Tacos
      </h2>
      <div className="space-y-4">
        <p className="font-prosto text-balance text-xs lg:text-md mb-2">
          Two delicious fresh fish tacos Baja-style — beer-battered with
          chipotle mayo, sour cream, cabbage and pico de gallo.
        </p>
        <p className="text-3xl lg:text-5xl text-maroon font-charles">$14</p>
      </div>
      <div className="mt-auto">
        <a href="/order" className="text-sm text-white font-prosto underline">
          Order Online
        </a>
      </div>
    </div>

    {/* Column 3 (Stacked images: top 2/3, bottom 1/3) 
       - On mobile, these will just appear as a separate row below. */}
    <div
      className="
        lg:col-span-1 
        grid 
        grid-rows-1 lg:grid-rows-3 
        grid-cols-2 lg:grid-cols-1
        gap-4 
        h-full 
        min-h-0
      "
    >
      {/* On mobile, we just place them in a single column. 
          On md+, row-span-2 => top tile = 2/3 height */}
      <div className="lg:row-span-2 overflow-hidden bg-maroon max-h-[17vh] lg:max-h-full">
        <img
          src="https://santaplaya-bucket.s3.amazonaws.com/bestFishTacos.webp"
          className="h-full w-full object-contain p-4"
        />
      </div>
      <div className="lg:row-span-1 overflow-hidden bg-basic max-h-[17vh] lg:max-h-full">
        <img
          src="https://santaplaya-bucket.s3.amazonaws.com/octo.webp"
          className="h-full w-full object-contain p-4"
        />
      </div>
    </div>
  </div>
);

/* ---------------- SignatureDishes2 ---------------- */
const SignatureDishes2: React.FC = () => (
  <div
    className="
      grid 
      grid-cols-1 lg:grid-cols-4 
      gap-4 
      h-auto lg:h-[50vh]      
      min-h-0        
      px-4 lg:px-24 
      pt-8 lg:pt-16
      relative
    "
  >
    {/* Column 1 (Title + Text) */}
    <div
      className="
        lg:col-span-1 
        bg-sea 
        p-4 lg:p-8
        flex 
        flex-col 
        h-full 
        min-h-0 
        overflow-hidden
        space-y-4
      "
    >
      <h2 className="text-3xl xl:text-5xl font-bold font-charles text-basic text-center mb-2 mt-4 xl:mt-12">
        Santa Playa Botana
      </h2>
      <div className="space-y-4">
        <p className="font-prosto text-balance text-xs lg:text-md mb-2">
          Shrimp (Cooked and Crudo) bluefin tuna, Octopus, Scallop And a mix of
          Ceviche.
        </p>
        <p className="text-3xl lg:text-5xl text-maroon font-charles">$34</p>
      </div>
      <div className="mt-auto">
        <a href="/order" className="text-sm text-white font-prosto underline">
          Order Online
        </a>
      </div>
    </div>

    {/* Column 3 (Stacked images: top 2/3, bottom 1/3) 
       - On mobile, these will just appear as a separate row below. */}
    <div
      className="
        lg:col-span-1 
        grid 
        grid-rows-1 lg:grid-rows-3 
        grid-cols-2 lg:grid-cols-1
        gap-4 
        h-full 
        min-h-0
      "
    >
      {/* On mobile, we just place them in a single column. 
          On md+, row-span-2 => top tile = 2/3 height */}
      <div className="lg:row-span-2 overflow-hidden bg-maroon max-h-[17vh] lg:max-h-full">
        <img
          src="https://santaplaya-bucket.s3.amazonaws.com/invisibleCrab.webp"
          className="h-full w-full object-contain p-4"
        />
      </div>
      <div className="lg:row-span-1 overflow-hidden bg-basic max-h-[17vh] lg:max-h-full">
        <img
          src="https://santaplaya-bucket.s3.amazonaws.com/octo.webp"
          className="h-full w-full object-contain p-4"
        />
      </div>
    </div>

    {/* Column 3 (Half width images on md+) */}
    <div className="lg:col-span-2 min-h-0">
      <img
        src="https://santaplaya-bucket.s3.amazonaws.com/SPBotana.webp"
        alt="Placeholder"
        className="h-full w-full object-cover lg:h-full"
      />
    </div>
  </div>
);

/* ---------------- Full SignatureDishes ---------------- */
const SignatureDishes: React.FC = () => {
  // Desktop parallax hooks
  const { ref: octopusRef } = useParallax<HTMLDivElement>({
    rotateX: [-90, 90],
    easing: 'easeInOut',
  });
  const { ref: prawnRef } = useParallax<HTMLImageElement>({
    rotate: [-15, 35],
    easing: 'easeInOut',
  });
  const { ref: fishRef } = useParallax<HTMLDivElement>({
    translateX: [0, 40],
    easing: 'easeInOut',
  });
  return (
    <>
      <div className="relative z-10">
        <SignatureDishes1 />
      </div>
      <div className="pointer-events-none flex lg:hidden w-full z-[-1]">
        {/* Left image */}
        <div className="relative w-[32vw] object-contain">
          <img
            src="https://santaplaya-bucket.s3.amazonaws.com/blue_octopus.webp"
            alt="Blue Octopus"
            className="absolute -bottom-8 -left-12"
          />
        </div>

        {/* Center image */}
        <img
          src="https://santaplaya-bucket.s3.amazonaws.com/blue_prawn.webp"
          alt="Blue Prawn"
          className="w-[8vw] object-contain"
        />

        {/* Right image */}
        <div className="relative w-[60vw] object-contain">
          <img
            src="https://santaplaya-bucket.s3.amazonaws.com/blue_fish.webp"
            alt="Blue Fish"
            className="absolute -bottom-8 -right-12"
          />
        </div>
      </div>
      {/* Wrapper for SignatureDishes2 and BG images */}
      <div className="relative">
        {/* Increase z-index so the background images sit behind */}
        <div className="relative z-10">
          <SignatureDishes2 />
        </div>

        {/* 
          Absolutely or relatively positioned backgrounds. 
          Hide them on mobile -> show on md+ with "hidden lg:flex"
        */}
        <div className="pointer-events-none hidden lg:flex w-full ">
          {/* Left image */}
          <div ref={octopusRef} className="relative w-[32vw] object-contain">
            <img
              src="https://santaplaya-bucket.s3.amazonaws.com/blue_octopus.webp"
              alt="Blue Octopus"
              className="absolute -bottom-8 -left-12"
            />
          </div>

          {/* Center image */}
          <img
            src="https://santaplaya-bucket.s3.amazonaws.com/blue_prawn.webp"
            alt="Blue Prawn"
            className="w-[8vw] object-contain"
            ref={prawnRef}
          />

          {/* Right image */}
          <div ref={fishRef} className="relative w-[60vw] object-contain">
            <img
              src="https://santaplaya-bucket.s3.amazonaws.com/blue_fish.webp"
              alt="Blue Fish"
              className="absolute -bottom-28 -right-12"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SignatureDishes;
